import { SelectorReturnType } from '@vfde-sails/types';
import {
    getCustomerFlow,
    getLoginData,
    getSiteStructure,
    PageViewTrackingData,
    TRACKING_PLATFORM_TYPE_RESPONSIVE,
    TRACKING_PRODUCT_LINE,
    TRACKING_PRODUCT_TYPE,
    TRACKING_SITE_AREA_MOBILITY,
    TrackingBtx,
    TrackingFlowType,
    TrackingLoginStatus,
    TrackingPageType,
    TrackingProduct,
    TrackingProductType,
} from '@vfde-sails/tracking';
import { selectTrackingPayload } from 'Container/App/selectors';
import { BTX_MULTISIM } from '@vfde-sails/constants';
import { PriceType } from '@vfde-sails/glados-v2';
import { decodeHtml } from '@vfde-sails/utils';
import {
    ONE_NUMBER_TARIFF_OPTION,
    TRACKING_PAGENAME,
} from 'Container/App/constants';

/**
 * Create the page view tracking object
 */
export const createPageViewTrackingData = ({
    customerConfig,
    subscription,
    priceToPay,
    atomic: atomicDevice,
    connectionFee,
}: SelectorReturnType<typeof selectTrackingPayload>): PageViewTrackingData | null => {
    if (!atomicDevice) {
        return null;
    }

    const { isHardwareOnly, salesChannel } = customerConfig;

    const siteStructure = getSiteStructure(TRACKING_PAGENAME, BTX_MULTISIM);
    const loginStatus = getLoginData() ? TrackingLoginStatus.LoggedIn : TrackingLoginStatus.NotLoggedIn;
    const lineOfCustomer = getCustomerFlow(salesChannel);
    const productCategory = isHardwareOnly ? TrackingProductType.Device : TrackingProductType.Bundle;

    const devicePriceOnce = isHardwareOnly
        ? atomicDevice.priceHardwareOnly.gross
        : atomicDevice.priceHardwareOneTimeWithTariff.gross;

    // This html-decodes the encoded umlauts from vlux into actual umlauts
    const atomicName = decodeHtml(atomicDevice.atomicName);
    let propositionName = atomicName;

    const aProducts: TrackingProduct[] = [{
        sku: atomicDevice.sku,
        name: atomicName,
        type: TrackingProductType.Device,
        priceOnce: `${devicePriceOnce || 0}`,
        proposition: 1,
        units: 1,
    }];

    if (customerConfig.paymentMethod !== undefined && !isHardwareOnly) {
        aProducts.push({
            sku: customerConfig.paymentMethod === PriceType.Monthly
                ? subscription!.tariffId
                : ONE_NUMBER_TARIFF_OPTION,
            name: customerConfig.paymentMethod === PriceType.Monthly
                ? subscription!.tariffName
                : 'Vodafone OneNumber (als eSIM)',
            type: TrackingProductType.Tariff,
            ...connectionFee && { priceOnce: `${connectionFee.gross}` },
            priceMonthly: `${priceToPay?.gross}`,
            duration: 24,
            proposition: 1,
            units: 1,
        });

        propositionName += ` | ${customerConfig.paymentMethod === PriceType.Monthly ? subscription!.tariffName : 'OneNumber'}`;
    }

    return {
        /* eslint-disable jsdoc/require-jsdoc, @typescript-eslint/naming-convention, camelcase */
        page_levels: siteStructure,
        login_status: loginStatus,
        flow_type: isHardwareOnly
            ? TrackingFlowType.HardwareOnly
            : TrackingFlowType.IotProductDetails,
        line_of_customer: lineOfCustomer,
        page_type: TrackingPageType.ProductDetail,
        site_area: TRACKING_SITE_AREA_MOBILITY,
        platform_type: TRACKING_PLATFORM_TYPE_RESPONSIVE,
        business_transaction_type: isHardwareOnly ? TrackingBtx.HardwareOnly : TrackingBtx.Iot,
        /* eslint-enable jsdoc/require-jsdoc, @typescript-eslint/naming-convention, camelcase */
        oOrder: {
            aProducts,
            aPropositions: [
                {
                    id: 1,
                    name: propositionName,
                    targetAudience: lineOfCustomer,
                    retention: isHardwareOnly ? TrackingBtx.HardwareOnly : TrackingBtx.Bnt,
                    productType: isHardwareOnly ? TrackingBtx.HardwareOnly : TRACKING_PRODUCT_TYPE,
                    productLine: isHardwareOnly ? TrackingBtx.HardwareOnly : TRACKING_PRODUCT_LINE,
                    productCategory,
                    units: 1,
                },
            ],
        },
    };
};
